import React, {forwardRef, useMemo, useState} from 'react';
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  message,
  Popconfirm,
  Popover,
  Table,
  TableProps,
  Tooltip,
  Typography
} from 'antd'
import {t} from "i18next";
import {DataChannel, DataFactor, DataTotal} from "../../../../api/types";
import {api} from "../../../../api/apiProvider";
import urls from "../../../../api/urls";
import IconXlsFile from "../../../ui/icons/IconXlsFile";
import {CaretRightOutlined, EditOutlined, InfoCircleOutlined, SaveOutlined} from "@ant-design/icons";
import ButtonCompare from "../../../ui/buttons/ButtonCompare";
import CalculationStatusTag from "./CalculationStatus";
import {CalculationStatus, DataTotalSource} from "../../../../api/constants";
import Roles from "../../../../roles/Roles";
import {KeyedMutator} from "swr";
import {TriggerWithArgs} from "swr/mutation";
import {TableMetrics} from "./filter/TableMetricsFilter";
import { renderNumberCell } from '../../../../numberFormat'

const {Text} = Typography;

type OnChange = NonNullable<TableProps<DataType>['onChange']>
type GetSingle<T> = T extends (infer U)[] ? U : never
type Sorts = GetSingle<Parameters<OnChange>[2]>

type DataField = 'budget' | 'kpi' | 'roas' | 'cac'

interface UserValue {
  label: string;
  value: string;
}

type TableMetric = keyof TableMetrics

interface DataTotalProps {
  dataTotal: DataTotal,
  isPeriodAccepted: boolean,
  isDataTotalsLoading: boolean,
  isDataTotalLoading: boolean,
  mutateDataTotals: KeyedMutator<{ data: DataTotal[]; }>,
  mutateDataTotal: TriggerWithArgs<{
    data: DataTotal
  }, any, string, number>,
  selectedPlanId: number,
  setSelectedPlanId: Function,
  setCurrentPlanId: Function,
  showForm: Function,
  filter: UserValue[],
  selectedForComparesIds: string[],
  setSelectedForCompares: Function,
  setShowFactData: Function,
  selectedMetrics: TableMetric[],
}

interface DataType {
  name: string | JSX.Element,
  budget: string | JSX.Element,
  cac: string | JSX.Element,
  kpi: string | JSX.Element,
  cumulative: string | JSX.Element,
  roas: string | JSX.Element,
  isTotal: boolean,
  headertype: string,
}

interface TableData {
  name: string | JSX.Element,
  budget: string | JSX.Element,
  cac: string | JSX.Element,
  kpi: string | JSX.Element,
  kpi_long_term: string | JSX.Element,
  kpi_short_term: string | JSX.Element,
  cumulative: string | JSX.Element,
  roas: string | JSX.Element,
  id: string,
  audienceText: string,
  managerText: string,
  isTotal: boolean,
  headertype: string,
}

const mediaType = 'media';
const nonMediaType = 'nonMedia';

const deleteDataTotal = async (id: number) => {
  return await api.delete(`${urls.dataTotals}/${id}`)
};

const updateName = async (id: number, data: any) => {
  return await api.patch(`${urls.dataTotals}/${id}`, {arg: data})
};

const DataTotalTable = forwardRef<HTMLDivElement, DataTotalProps>(
  ({
     dataTotal,
     isPeriodAccepted,
     selectedPlanId,
     setSelectedPlanId,
     setCurrentPlanId,
     showForm,
     filter,
     selectedForComparesIds,
     setSelectedForCompares,
     setShowFactData,
     isDataTotalsLoading,
     mutateDataTotals,
     mutateDataTotal,
     isDataTotalLoading,
     selectedMetrics,
   }, ref) => {
    const [isEditingName, setIsEditingName] = useState<boolean>(false);
    const [isNameUpdating, setIsNameUpdating] = useState<boolean>(false);
    const [isMediaExpanded, setIsMediaExpanded] = useState<boolean>(true);
    const [isNonMediaExpanded, setIsNonMediaExpanded] = useState<boolean>(false);
    const [name, setName] = useState<string>(dataTotal.name);
    const [sorter, setSorter] = useState<Sorts>({});

    const isCurrentPlanAccepted = () => {
      return dataTotal.id === selectedPlanId
    };

    const showCheckbox = () => {
      return (!isPeriodAccepted || isCurrentPlanAccepted()) && !dataTotal.isFact && Roles.hasAccess('canAcceptPlan')
    };

    const removeDataTotal = () => {
      new Promise((resolve) => {
        deleteDataTotal(dataTotal.id).then(() => {
          mutateDataTotals((currentData: any) => {
            if (!currentData?.data) {
              return currentData;
            }
            const filteredData = currentData.data.filter((item: DataTotal) => item.id !== dataTotal.id);

            return {
              ...currentData,
              data: filteredData
            };
          }, false);

          mutateDataTotals((currentData: any) => {
            if (!currentData?.data) {
              return currentData;
            }

            const hasFactData = currentData.data.some((item: DataTotal) => item.isFact);

            if (dataTotal.isFact && !hasFactData) {
              setShowFactData(false);
            }

            return currentData;
          }, false);

        }).catch((response) => {
          message.error(response['hydra:description']);
          mutateDataTotals();
        });
      });
    };

    const saveName = () => {
      setIsNameUpdating(true);

      updateName(dataTotal.id, {
        name: name,
      }).then(async () => {
        await mutateDataTotal(dataTotal.id);
        setIsEditingName(false);
        setIsNameUpdating(false)
      }).catch((response) => {
        message.error(response['hydra:description']);
        setIsEditingName(false);
        setName(dataTotal.name);
        setIsNameUpdating(false)
      })
    };

    const handleCompareClick = () => {
      if (selectedForComparesIds.includes(dataTotal["@id"])) {
        setSelectedForCompares([...selectedForComparesIds.filter(function (compareItemId, index, arr) {
          return compareItemId !== dataTotal["@id"]
        })])
      } else {
        if (!selectedForComparesIds.includes(dataTotal["@id"])) {
          setSelectedForCompares([
            ...selectedForComparesIds,
            dataTotal["@id"],
          ])
        }
      }
    };

    const isInCompare = () => {
      return selectedForComparesIds.includes(dataTotal["@id"]);
    };

    const isMediaGroupExpanded = (record: DataType) => record.headertype === mediaType && isMediaExpanded;
    const isNonMediaGroupExpanded = (record: DataType) => record.headertype === nonMediaType && isNonMediaExpanded;

    const columns: TableProps<DataType>['columns'] = [
      {
        title: t('planner.channel'),
        dataIndex: 'name',
        key: 'name',
        width: '20%',
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 9};
          }
          return {};
        },
      },
      {
        title: <Popover placement="top" content={
          <div>{t('planner.kpiInTotalsFull')}</div>
        } arrow overlayClassName={'sider-links__popover'}>
          {t('planner.kpiInTotals')}
        </Popover>,
        dataIndex: 'kpi',
        key: 'kpi',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.kpi) - Number(b.kpi),
        },
        sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
        onCell: (record, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }

          return {};
        },
        hidden: !selectedMetrics.includes('kpi'),
      },
      {
        title: <Popover placement="top" content={
          <div>{t('planner.kpiShortTermFull')}</div>
        } arrow overlayClassName={'sider-links__popover'}>
          {t('planner.kpiShortTerm')}
        </Popover>,
        dataIndex: 'kpi_short_term',
        key: 'kpi_short_term',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.kpi) - Number(b.kpi),
        },
        sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
        onCell: (record, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }

          return {};
        },
        hidden: !selectedMetrics.includes('kpi_short_term'),
      },
      {
        title: <Popover placement="top" content={
          <div>{t('planner.kpiLongTermFull')}</div>
        } arrow overlayClassName={'sider-links__popover'}>
          {t('planner.kpiLongTerm')}
        </Popover>,
        dataIndex: 'kpi_long_term',
        key: 'kpi_long_term',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.kpi) - Number(b.kpi),
        },
        sortOrder: sorter.columnKey === 'kpi' ? sorter.order : null,
        onCell: (record, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }

          return {};
        },
        hidden: !selectedMetrics.includes('kpi_long_term'),
      },
      {
        title: t('planner.cumulative'),
        dataIndex: 'cumulative',
        key: 'cumulative',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.cumulative) - Number(b.cumulative),
        },
        sortOrder: sorter.columnKey === 'cumulative' ? sorter.order : null,
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }
          return {};
        },
        hidden: !selectedMetrics.includes('cumulative'),
      },
      {
        title: t('planner.budget'),
        dataIndex: 'budget',
        key: 'budget',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.budget) - Number(b.budget),
        },
        sortOrder: sorter.columnKey === 'budget' ? sorter.order : null,
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }
          return {};
        },
        hidden: !selectedMetrics.includes('budget'),
      },
      /*{
        label: t('planner.metrics'),
        code: 'metrics',
      },*/
      {
        title: t('planner.roas'),
        dataIndex: 'roas',
        key: 'roas',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.roas) - Number(b.roas),
        },
        sortOrder: sorter.columnKey === 'roas' ? sorter.order : null,
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }
          return {};
        },
        hidden: !selectedMetrics.includes('roas'),
      },
      /*{
        title: t('planner.cac'),
        dataIndex: 'cac',
        key: 'cac',
        width: '15%',
        sorter: {
          compare: (a, b) => Number(a.cac) - Number(b.cac),
        },
        sortOrder: sorter.columnKey === 'cac' ? sorter.order : null,
      },*/
      {
        title: t('factors.audienceText'),
        dataIndex: 'audienceText',
        key: 'audienceText',
        width: '10%',
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }
          return {};
        },
        hidden: !selectedMetrics.includes('audienceText'),
      },
      {
        title: t('factors.managerText'),
        dataIndex: 'managerText',
        key: 'managerText',
        width: '10%',
        onCell: (record: DataType, index) => {
          if (isMediaGroupExpanded(record) || isNonMediaGroupExpanded(record)) {
            return {colSpan: 0};
          }
          return {};
        },
        hidden: !selectedMetrics.includes('managerText'),
      },
    ];

    const data = useMemo(() => {
      let dataChannels: DataChannel[] = dataTotal.dataChannels.filter((dataChannel: DataChannel) => dataChannel.channel.isActive);

      if (sorter.columnKey && sorter.order) {
        const key: DataField = String(sorter.columnKey) as DataField;
        const order = String(sorter.order);

        dataChannels.sort((a, b) =>
          order === 'ascend' ? (Number(a.metrics[key]) - Number(b.metrics[key])) : (Number(b.metrics[key]) - Number(a.metrics[key])))
      }

      const data: TableData[] = [];

      if (dataChannels.length > 0) {
        data.push({
          name: <Flex
            justify={'space-between'}
            align={'center'}
            className={'data-total__group-title' + (isMediaExpanded ? ' expanded' : '')}
            onClick={() => setIsMediaExpanded(!isMediaExpanded)}
          >
            <Flex>{t('planner.media')} ({dataChannels.length})</Flex>
            <Flex>
              <span style={{color: '#7E8681'}}><CaretRightOutlined rotate={isMediaExpanded ? 90 : 0}/></span>
            </Flex>
          </Flex>,
          isTotal: false,
          budget: renderNumberCell(dataTotal.metrics?.media?.budget),
          cac: renderNumberCell(dataTotal.metrics?.media?.cac),
          kpi: renderNumberCell(dataTotal.metrics?.media?.kpi),
          kpi_short_term: renderNumberCell(dataTotal.metrics?.media?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataTotal.metrics?.media?.kpi_long_term),
          cumulative: renderNumberCell(dataTotal.metrics?.media?.cumulative),
          roas: renderNumberCell(dataTotal.metrics?.media?.roas, 1),
          id: 'media_group',
          audienceText: '',
          managerText: '',
          headertype: mediaType,
        })
      }

      if (isMediaExpanded) {
        for (let dataChannel of dataChannels) {
          data.push({
            name: dataChannel.name,
            budget: renderNumberCell(dataChannel.metrics?.budget),
            cac: renderNumberCell(dataChannel.metrics?.cac),
            kpi: renderNumberCell(dataChannel.metrics?.kpi),
            kpi_short_term: renderNumberCell(dataChannel.metrics?.kpi_short_term),
            kpi_long_term: renderNumberCell(dataChannel.metrics?.kpi_long_term),
            cumulative: renderNumberCell(null),
            roas: renderNumberCell(dataChannel.metrics?.roas, 1),
            id: dataChannel["@id"],
            audienceText: dataChannel?.channel?.audienceText || '',
            managerText: dataChannel?.channel?.managerText || '',
            isTotal: false,
            headertype: '',
          })
        }

        data.push({
          name: t('planner.allMedia'),
          budget: renderNumberCell(dataTotal.metrics?.media?.budget),
          cac: renderNumberCell(dataTotal.metrics?.media?.cac),
          kpi: renderNumberCell(dataTotal.metrics?.media?.kpi),
          kpi_short_term: renderNumberCell(dataTotal.metrics?.media?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataTotal.metrics?.media?.kpi_long_term),
          cumulative: renderNumberCell(dataTotal.metrics?.media?.cumulative),
          roas: renderNumberCell(dataTotal.metrics?.media?.roas, 1),
          id: 'total_media',
          audienceText: '',
          managerText: '',
          isTotal: false,
          headertype: '',
        })
      }

      let dataFactors: DataFactor[] = dataTotal.nonMediaDataFactors
        ? dataTotal.nonMediaDataFactors
        : [];

      if (sorter.columnKey && sorter.order) {
        const key: DataField = String(sorter.columnKey) as DataField;
        const order = String(sorter.order);

        dataFactors.sort((a, b) =>
          order === 'ascend' ? (Number(a?.metrics?.[key]) - Number(b?.metrics?.[key])) : (Number(b?.metrics?.[key]) - Number(a?.metrics?.[key])))
      }

      if (dataFactors.length > 0) {
        data.push({
          name: <Flex
            justify={'space-between'}
            align={'center'}
            className={'data-total__group-title' + (isNonMediaExpanded ? ' expanded' : '')}
            onClick={() => setIsNonMediaExpanded(!isNonMediaExpanded)}
          >
            <Flex>{t('planner.nonMedia')} ({dataFactors.length})</Flex>
            <Flex>
              <span style={{color: '#7E8681'}}><CaretRightOutlined rotate={isNonMediaExpanded ? 90 : 0}/></span>
            </Flex>
          </Flex>,
          isTotal: false,
          budget: renderNumberCell(dataTotal.metrics?.nonMedia?.budget),
          cac: renderNumberCell(dataTotal.metrics?.nonMedia?.cac),
          kpi: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi),
          kpi_short_term: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi_long_term),
          cumulative: renderNumberCell(dataTotal.metrics?.nonMedia?.cumulative),
          roas: renderNumberCell(dataTotal.metrics?.nonMedia?.roas, 1),
          id: 'non_media_group',
          audienceText: '',
          managerText: '',
          headertype: nonMediaType,
        })
      }

      if (isNonMediaExpanded) {
        for (let dataFactor of dataFactors) {
          data.push({
            name: dataFactor.factor.name,
            budget: renderNumberCell(dataFactor.metrics?.budget),
            cac: renderNumberCell(dataFactor.metrics?.cac),
            kpi: renderNumberCell(dataFactor.metrics?.kpi),
            kpi_short_term: renderNumberCell(dataFactor.metrics?.kpi_short_term),
            kpi_long_term: renderNumberCell(dataFactor.metrics?.kpi_long_term),
            cumulative: renderNumberCell(null),
            roas: renderNumberCell(dataFactor.metrics?.roas, 1),
            id: dataFactor["@id"],
            audienceText: dataFactor?.factor?.audienceText || '',
            managerText: dataFactor?.factor?.managerText || '',
            isTotal: false,
            headertype: '',
          })
        }

        data.push({
          name: t('planner.allNonMedia'),
          budget: renderNumberCell(dataTotal.metrics?.nonMedia?.budget),
          cac: renderNumberCell(dataTotal.metrics?.nonMedia?.cac),
          kpi: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi),
          kpi_short_term: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataTotal.metrics?.nonMedia?.kpi_long_term),
          cumulative: renderNumberCell(dataTotal.metrics?.nonMedia?.cumulative),
          roas: renderNumberCell(dataTotal.metrics?.nonMedia?.roas, 1),
          id: 'total_non_media',
          audienceText: '',
          managerText: '',
          isTotal: false,
          headertype: '',
        })
      }

      if (dataTotal.dataChannels.length === filter.length || filter.length === 0) {
        data.push({
          name: t('planner.total'),
          budget: renderNumberCell(dataTotal.metrics?.total?.budget),
          cac: renderNumberCell(dataTotal.metrics?.total?.cac),
          kpi: renderNumberCell(dataTotal.metrics?.total?.kpi),
          kpi_short_term: renderNumberCell(dataTotal.metrics?.total?.kpi_short_term),
          kpi_long_term: renderNumberCell(dataTotal.metrics?.total?.kpi_long_term),
          cumulative: renderNumberCell(dataTotal.metrics?.total?.cumulative),
          roas: renderNumberCell(dataTotal.metrics?.total?.roas, 1),
          id: dataTotal["@id"],
          audienceText: '',
          managerText: '',
          isTotal: true,
          headertype: '',
        })
      }

      return data
    }, [dataTotal, filter, sorter, isMediaExpanded, isNonMediaExpanded]);

    const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra) => {
      setSorter(sorter as Sorts)
    };

    return (
      <Card
        title={<Flex style={{width: '100%'}} gap={'small'} align={'center'} justify={'space-between'}>
          <Flex gap={'small'}>
            <Flex style={{minWidth: 300}} gap={'small'}>
              {showCheckbox() &&
                <Checkbox
                  checked={selectedPlanId === dataTotal.id}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setSelectedPlanId(dataTotal.id)
                    } else {
                      setSelectedPlanId(0)
                    }
                  }}
                  disabled={isPeriodAccepted || dataTotal.calculationStatus !== CalculationStatus.SUCCESS}
                />
              }
              {!isEditingName &&
                <Text
                  style={{maxWidth: 300}}
                  ellipsis={{tooltip: dataTotal.name}}
                >
                  {dataTotal.name}
                </Text>
              }
              {isEditingName &&
                <Input
                  value={name}
                  onChange={e => {
                    setName(e.target.value)
                  }}
                  size={'small'}
                  style={{minWidth: '300px', flex: 1}}
                />
              }

              {!isEditingName &&
                <>
                  <Tooltip title={t('common.edit')}>
                    <Button
                      icon={<EditOutlined/>}
                      size={'small'}
                      onClick={() => setIsEditingName(true)}
                    ></Button>
                  </Tooltip>
                </>
              }

              {isEditingName &&
                <Tooltip title={t('common.save')}>
                  <Button
                    icon={<SaveOutlined/>}
                    size={'small'}
                    onClick={() => saveName()}
                    loading={isNameUpdating || isDataTotalLoading}
                  ></Button>
                </Tooltip>
              }
            </Flex>

            <CalculationStatusTag status={dataTotal.calculationStatus} dataTotalId={dataTotal.id}/>
          </Flex>
          <Flex>
            {dataTotal.file && dataTotal.file.id &&
              <a className={'media-factors__file'}
                 onClick={() => {
                   dataTotal.file && dataTotal.file.contentUrl &&
                   api.getFile(`${process.env.REACT_APP_PUBLIC_ENTRYPOINT}${dataTotal.file.contentUrl}`)
                 }}
              >
                <Flex justify={'space-between'} align={'center'} gap={'small'}>
                  <IconXlsFile/>
                  {dataTotal.file && dataTotal.file.originalName}
                </Flex>
              </a>
            }
          </Flex>
          <Flex align={'center'}>
            {dataTotal.calculationStatus === CalculationStatus.SUCCESS &&
              <ButtonCompare
                size={'small'}
                compared={isInCompare()}
                onClick={handleCompareClick}
              />
            }
            {dataTotal.dataSource === DataTotalSource.OPTIMIZATION &&
              <Button
                icon={<InfoCircleOutlined/>}
                type="text"
                onClick={() => {
                  setCurrentPlanId(dataTotal.id);
                  showForm()
                }}
              ></Button>
            }

            {!isCurrentPlanAccepted() &&
              <Popconfirm
                placement="bottom"
                title={t('common.delete')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={() => removeDataTotal()}
              >
                <Button
                  type="text"
                  className={'variant-table__delete-button'}
                >
                  {t('common.delete')}
                </Button>
              </Popconfirm>
            }
          </Flex>
        </Flex>}
        classNames={{
          body: 'data-total__card-body'
        }}
        ref={ref}
      >
        {dataTotal.calculationStatus === CalculationStatus.SUCCESS &&
          <Table
            className={'variant-table ' + (isCurrentPlanAccepted() ? 'variant-table__accepted' : '')}
            style={{width: '100%'}}
            columns={columns}
            dataSource={data}
            pagination={false}
            bordered
            rowKey={'id'}
            onChange={onChange}
            loading={isDataTotalsLoading}
            rowClassName={(record: DataType) => record.isTotal ? 'total-row' : ''}
            showSorterTooltip={false}
          />
        }
      </Card>
    )
  }
);

DataTotalTable.displayName = 'Variant';
export default DataTotalTable
